import React, { useState, useEffect, useCallback } from "react";
import {
  apiFetchTopWeeklySkitChart,
  apiFetchTopWeeklySkitChartRatings,
} from "../../api/backend/skits";
import { SkitsOlympicLogoIcon, FilterIcon } from "../../components/icons";
import FlipCounter from "../../components/flipCounter/flipCounterZero";
import { getEmojiByName } from "../../utils/emojiUtils";
import {
  SmallPlainInsetBtn,
  SmallPlainInsetBtnIcon,
} from "../../components/buttons";
import { channelsFilters, skitsRankingsFilters } from "../../constants/categories";
import RatingMetric from "../../components/ratingMetric/ratingMetric";
import { quantifyNumEnding } from "../../utils/formatNumber";
import FilterPopup from "../../components/filterPopup/filterPopup";
import { useLocalStorage } from "../../components/hooks/useLocalStorage";
import { EyeIcon } from "../../components/icons";
import Loader from "../../components/loader/loader";
import "./top100Skits.scss";

function Top100Skits() {
  // Instead of normal useState, store these in localStorage so you see them easily.
  const [activeCategory, setActiveCategory] = useLocalStorage("activeCategory", 500);
  const [rankingMetric, setRankingMetric] = useLocalStorage(
    "rankingMetric",
    "claps"
  );
  const [filterParams, setFilterParams] = useLocalStorage("filterParams", {});
  const [skitChartYearWeekNum, setSkitChartYearWeekNum] = useLocalStorage(
    "skitChartYearWeekNum",
    ""
  );

  // For data/pagination state, you can still use normal state or localStorage as desired.
  const [skits, setSkits] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [showFilterPopup, setShowFilterPopup] = useState(false);

  const limitVal = 10; // number of records per page

  // --- API FETCH FUNCTION ---
  const fetchSkits = useCallback(
    async (reset = false) => {
      const currentOffset = reset ? 0 : offset;
      const params = {
        offset: currentOffset,
        limit: limitVal,
        categoryid: activeCategory,
        ...filterParams,
      };

      console.log(
        "Fetching skits with params:",
        params,
        "| Current Metric:",
        rankingMetric
      );

      try {
        let responseData;
        if (rankingMetric === "ratings") {
          console.log("Calling API endpoint for ratings");
          responseData = await apiFetchTopWeeklySkitChartRatings(params);
        } else {
          console.log("Calling API endpoint for claps (default)");
          responseData = await apiFetchTopWeeklySkitChart(params);
        }
        console.log("Response data received:", responseData);

        if (responseData.success && responseData.data) {
          const newSkits = responseData.data.skits;
          if (reset) {
            const newFilterParams = {
              year: responseData.data.year,
              week: responseData.data.weekNum,
            };
            // Compare JSON stringified objects. (A simple deep-equality check for small objects.)
            if (
              JSON.stringify(newFilterParams) !== JSON.stringify(filterParams)
            ) {
              setFilterParams(newFilterParams);
              setSkitChartYearWeekNum(
                `Week ${responseData.data.weekNum}, ${responseData.data.year}`
              );
            }
          }

          setSkits((prev) => (reset ? newSkits : [...prev, ...newSkits]));
          setHasMore(responseData.data.moreSkitsAvailable);
          setOffset(currentOffset + limitVal);
        } else {
          console.log("Unsuccessful response or missing data from API.");
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching top weekly skits:", error);
        setHasMore(false);
      }
      setLoading(false);
    },
    [activeCategory, filterParams, rankingMetric, limitVal, offset]
  );

  // Fetch on mount, plus whenever activeCategory/rankingMetric/filterParams change.
  useEffect(() => {
    fetchSkits(true);
  }, [fetchSkits]);

  // --- EVENT HANDLERS ---

  // Metric button clicked
  const handleMetricClick = (metricValue) => {
    console.log("Metric button clicked. New Metric:", metricValue);
    setRankingMetric(metricValue);
    setOffset(0);
    setSkits([]);
    setHasMore(true);
  };

  // Category change
  const handleCategoryChange = (categoryId) => {
    console.log("Changing category to:", categoryId);
    setActiveCategory(categoryId);
    setOffset(0);
    setSkits([]);
    setHasMore(true);
    fetchSkits(true);
  };

  // Filters from the FilterPopup
  const handleApplyFilters = (newTimeFilters) => {
    console.log("Applying time filters:", newTimeFilters);
    const updatedFilters = {
      week: newTimeFilters.week,
      year: newTimeFilters.year,
    };
    setFilterParams(updatedFilters);
    setSkitChartYearWeekNum(
      `Week ${newTimeFilters.week}, ${newTimeFilters.year}`
    );
    setOffset(0);
    setSkits([]);
    setHasMore(true);
    fetchSkits(true);
  };

  return (
    <div id="skits-ranking">
      <div className="skitsChartsWrapper">
        <SkitsOlympicLogoIcon fillColor="var(--color-darkGold)" />
        <p className="skits-ranking-chart-header">SkitsIcon Trending Top Performers</p>
        <FlipCounter />
      </div>

      <div className="tabs-and-filter">
        <div className="category-tabs">
          {channelsFilters.map(({ id, name, Icon }) => {
            const isActive = activeCategory == id;
            return (
              <SmallPlainInsetBtnIcon
                key={id}
                text={name}
                icon={Icon}
                onClick={() => handleCategoryChange(id)}
                className={`tab-button ${isActive ? "active" : ""}`}
                disabled={isActive}
                iconFillColor="#b03a2e"
              />
            );
          })}
        </div>
        <div className="filter-button-container">
          <SmallPlainInsetBtnIcon
            icon={FilterIcon}
            width="210"
            text="Filter Weeks"
            onClick={() => setShowFilterPopup(true)}
          />
          {skitsRankingsFilters.map(({ id, name, Icon }) => {
            const filterValue = name.toLowerCase(); // Ensure this matches how you test your rankingMetric.
            const isActive = rankingMetric === filterValue;
            return (
              <SmallPlainInsetBtnIcon
                key={id}
                icon={Icon}
                width="210"
                text={`Performance by ${name}`}
                onClick={() => handleMetricClick(filterValue)}
                className={`filter-button ${isActive ? "active" : ""}`}
                disabled={isActive}
                iconFillColor="#b03a2e"
              />
            );
          })}
        </div>
      </div>
      {showFilterPopup && (
        <FilterPopup
          initialFilterOptions={filterParams}
          skitChartYearWeekNum={skitChartYearWeekNum}
          onClose={() => setShowFilterPopup(false)}
          onApplyTimePeriod={handleApplyFilters}
        />
      )}
      <div className="skits-grid">
        {skits.map((skit, index) => {
          const starRating = (skit.weeklyChartPlacementRatingSum * 5).toFixed(
            2
          );
          return (
            <div className="skit-card" key={skit.id}>
              <div className="skit-poster">
                <img
                  src={skit.skitThumbNailPosterImgUrl}
                  alt={skit.title}
                  onError={(e) => (e.target.src = "/images/defaultPoster.jpg")}
                />
                <div className="ranking-badge">{index + 1}</div>
              </div>
              <div className="skit-info">
                <h3>{skit.title}</h3>
                <div className="skit-metrics">
                  <RatingMetric
                    starRating={starRating}
                    ratingCount={skit.weeklyChartPlacementRatingCount}
                    skit={skit}
                  />
                  <span className="metric">
                    <span className="icon-container">
                      <span className="emoji">{getEmojiByName("Clapped")}</span>
                    </span>
                    <span className="metric-value">
                      {quantifyNumEnding(skit.weeklyChartPlacementClaps)}
                    </span>
                  </span>
                  <span className="metric">
                    <span className="icon-container">
                      <span className="emoji">{getEmojiByName("looking")}</span>
                    </span>
                    <span className="metric-value">
                      {quantifyNumEnding(skit.weeklyChartPlacementViews)}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Wrap the Loader and Button in a flex container */}
      <div className="pagination-controls">
        {loading && <Loader />}
        {hasMore && !loading && (
          <SmallPlainInsetBtn
            image={<EyeIcon fillColor="var(--color-bloodred)" />}
            text="View More"
            className="load-more-rankings-skits"
            onClick={() => fetchSkits()}
          />
        )}
      </div>
    </div>
  );
}

export default Top100Skits;
